import React, { useMemo } from "react";

import type { TaxSectionDTO } from "../data/taxesApi";
import { ComboPicker, type ComboOption } from "./ComboPicker";

/**
 * Choosing one of sixty-odd Income Tax sections.
 *
 * The widget itself is `ComboPicker`, shared with the currency step. What is
 * here is the part that is about tax: each section is known by three names —
 * the new section number, the description, and the Act 1961 number it replaced
 * — and all three are shown and searched, because which one a CA is thinking of
 * is not knowable in advance.
 *
 * The closed control shows the description, since that is what identifies the
 * rate being written, with the section number under it.
 */
export function SectionPicker({
  sections,
  value,
  onChange,
  disabled,
  placeholder = "Select a section",
}: {
  sections: TaxSectionDTO[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const options = useMemo<ComboOption[]>(
    () =>
      sections.map((section) => ({
        value: section.value,
        label: section.section_no || section.value,
        detail: section.description,
        note: section.old_section_no ? `Earlier: ${section.old_section_no}` : undefined,
        button: section.description || section.section_no,
        closed:
          section.section_no +
          (section.old_section_no ? ` · was ${section.old_section_no}` : ""),
        search: [section.description, section.section_no, section.old_section_no, section.value],
      })),
    [sections]
  );

  return (
    <ComboPicker
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      searchPlaceholder="Search - rent, 194H, 393(1)…"
      noun="sections"
    />
  );
}
